/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { ACTION_STRIP_HIGHLIGHT_LEFT_OFFSET } from '../../../config';
import { DimensionScaler } from '../../../core/device/DimensionScaler';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { ContentType } from '../../../core/enums/ContentType';
import { ServicesClientImpl } from '../../../core/interactors';
import { NdvrInteractor } from '../../../core/interactors/NdvrInteractor';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { Log } from '../../../core/logging';
import { ScreenController } from '../../../core/navigation/ScreenController';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { TStorageInfo, TVideoPlayerScreenProperties } from '../../../core/types';
import { RecordingRestriction } from '../../../core/usecases';
import { BrandingUiUtility } from '../../../core/utils';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { PlaybackRestrictionType } from '../../../core/videoplayer/enums';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { prepareCreateOnePassOverlay } from '../../overlay/createonepassoverlay/createonepassoverlay.reducres';
import { prepareModifyOnePassOverlay } from '../../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';
import { showEnterPCPinOverlay } from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    showRecordingOverlay,
    showStopAndDeleteRecordingOverlay,
} from '../../overlay/recordingoverlay/recordingoverlay.reducers';
import { prepareVodResumeStartOverOverlayFromLiveTv } from '../../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import { programScreenTransition } from '../../screens/navigation/screensnavigation';
import { toastMessageShowAction } from '../../toastmessage/toastmessage.actions';
import { showAudioTrackOverlay } from '../../videoplayer/overlays/audiotrackoverlay/audiotrackoverlay.reducers';
import { showSubtitleAndCCLanguageOverlay } from '../../videoplayer/overlays/subtitleandcclanguageoverlay/subtitleandcclanguageoverlay.reducers';
import { showVideoDebugOverlay } from '../../videoplayer/videodebugoverlay/videodebugoverlay.reducers';
import {
    isAdultAndUnsubscribedContentPlaybackActive,
    showPCPinOverlayForVideoPlayer,
    toggleIsPlaying,
    triggerPlaybackOnPinVerify,
} from '../../videoplayer/videoplayer.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import { hideFullInfo, setActionsStripHighlightIndex } from './infobanner.reducers';

const TAG: string = 'VideoPlayerActionsStripNavigation';

export const onKeyLeftActionsStrip = () => async (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { actionsStripHighlightIndex, actionItemsCount, actionStripReference } = infoBanner;
    if (actionsStripHighlightIndex <= 0) {
        // Circular action, go to last item
        const lastItemIndex = actionItemsCount > 0 ? actionItemsCount - 1 : 0;
        await dispatch(setActionsStripHighlightIndex(lastItemIndex));
        updateActionStripMarginByIndex(lastItemIndex, actionStripReference);

        return;
    }

    const nextIndex = actionsStripHighlightIndex - 1;
    await dispatch(setActionsStripHighlightIndex(nextIndex));

    updateActionStripMarginByIndex(nextIndex, actionStripReference);
};

export const onKeyOkActionsStrip = () => async (dispatch: Function, getState: Function) => {
    const bookmarkSharedPrefs = new SharedPreferences(SharedPrefConstants.BOOKMARK_ACTION_PREF_KEY);

    const bookmarkCollectionId = bookmarkSharedPrefs.getString(
        SharedPrefConstants.BOOKMARK_COLLECTION_ID,
    );
    const bookmarkContentId = bookmarkSharedPrefs.getString(
        SharedPrefConstants.BOOKMARK_CONTENT_ID,
    );

    const bookmarkKernelType = bookmarkSharedPrefs.getString(
        SharedPrefConstants.BOOKMARK_ACTION_KERNEL_TYPE,
    );

    const bookmarkType = Locale.getString(LocaleKeys[bookmarkKernelType]);

    dispatch(hideFullInfo());
    const { availableActionItems, actionsStripHighlightIndex } = getState().infoBanner;
    const ndvrInteractor = new NdvrInteractor();

    switch (availableActionItems[actionsStripHighlightIndex].name) {
        case `${Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_BOOKMARK)} ${bookmarkType}`:
            {
                const whisperMessage = Locale.getString(
                    LocaleKeys[`BOOKMARK_WHISPER_MESSAGE_FOR_${bookmarkKernelType}_TYPE`],
                );
                try {
                    ndvrInteractor
                        .getRequestModelToCreateBookmarkContentIdSchedule(
                            bookmarkContentId,
                            bookmarkCollectionId,
                        )
                        .send();
                    dispatch(
                        showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }),
                    );
                } catch (error) {
                    dispatch(
                        showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }),
                    );
                }
            }
            break;
        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_CHANGE_AUDIO_TRACK):
            dispatch(showAudioTrackOverlay());
            break;
        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_CHANGE_SUBTITLES_CC_LANGUAGE):
            dispatch(showSubtitleAndCCLanguageOverlay());
            break;
        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_CREATE_ONEPASS):
            dispatch(prepareCreateOnePassOverlay());
            break;
        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_MODIFY_ONEPASS):
            dispatch(prepareModifyOnePassOverlay());
            break;
        case ndvrInteractor.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER):
            if (
                isAdultContentPlaybackActive() &&
                VideoPlaybackController.getInstance().isNetworkConnected()
            ) {
                dispatch(showPCPinOverlayForVideoPlayer(startPlaybackAndResumeStartOver));
            } else {
                dispatch(prepareVodResumeStartOverOverlayFromLiveTv());
            }
            break;

        case Locale.getString(LocaleKeys.RR_RECORDING_NOT_PERMITTED_TEXT): {
            const services = new ServicesClientImpl();
            const restrictionDetails = RecordingRestriction.getRestrictionDetails(
                RecordingRestriction.ACTION_STRIP_KEY_OK,
                VideoPlaybackController.getInstance().getPlayable()?.channel,
            );
            const formattedMessage = BrandingUiUtility.formatDescription(
                restrictionDetails.message,
                {
                    CUSTOMER_SUPPORT_NAME: services.getBrandingUiInfoForKey(
                        BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
                    ),
                    PHONE_CUSTOMER_SUPPORT_NUMBER: services.getBrandingUiInfoForKey(
                        BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
                    ),
                },
            );
            dispatch(toastMessageShowAction(formattedMessage));
            break;
        }

        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_RECORD):
            if (
                isAdultContentPlaybackActive() &&
                VideoPlaybackController.getInstance().isNetworkConnected()
            ) {
                dispatch(showEnterPCPinOverlay(startPlaybackAndShowRecordOverlay));
            } else {
                dispatch(showRecordOverlay());
            }
            break;

        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_RESUME):
            if (
                VideoPlaybackController.getInstance().isPlaybackStarted() &&
                VideoPlaybackController.getInstance().isNetworkConnected()
            ) {
                dispatch(toggleIsPlaying());
            }
            break;

        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_SEE_MORE_INFO): {
            // Handle adult and unsubscribed content case
            if (
                isAdultAndUnsubscribedContentPlaybackActive() &&
                new ServicesClientImpl().shouldHideMetadata()
            ) {
                dispatch(
                    showPCPinOverlayForVideoPlayer(() => handleSeeMoreInfoTransition(dispatch)),
                );
            } else {
                handleSeeMoreInfoTransition(dispatch);
            }
            break;
        }
        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_STOP_RECORDING): {
            const { recordType } = getSharedPreferencesAndRecordType();
            dispatch(showStopAndDeleteRecordingOverlay(recordType));
            break;
        }
        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_TURN_SUBTITLES_CC_ON):
        case Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_TURN_SUBTITLES_CC_OFF):
            VideoPlaybackController.getInstance().toggleClosedCaption();
            break;
        case Locale.getString(LocaleKeys.LIVE_TV_STREAM_STATS):
            dispatch(showVideoDebugOverlay());
            break;

        default:
        // callback to default case
    }
};

const isAdultContentPlaybackActive = (): boolean => {
    return (
        VideoPlaybackController.getInstance().getRestrictionType() === PlaybackRestrictionType.ADULT
    );
};

const handleSeeMoreInfoTransition = (dispatch: Function) => {
    const currentScreen = ScreenController.getInstance().getScreen<TVideoPlayerScreenProperties>();
    const currentScreenProperties =
        currentScreen.getProperties() ?? ({} as TVideoPlayerScreenProperties);

    currentScreen.setProperties({
        ...currentScreenProperties,
        isForwardDirection: true,
    });
    VideoPlaybackController.getInstance().updateLastViewedPosition();
    dispatch(transitionToProgramScreenFromPlaybackScreen());
};

/** Starts playback and show record overlay on the top of Video player. */
const startPlaybackAndShowRecordOverlay = () => (dispatch: Function) => {
    dispatch(triggerPlaybackOnPinVerify());
    dispatch(showRecordOverlay());
};

/** Starts playback and resume start over or show start over option overlay. */
const startPlaybackAndResumeStartOver = () => async (dispatch: Function) => {
    await dispatch(triggerPlaybackOnPinVerify());
    await dispatch(prepareVodResumeStartOverOverlayFromLiveTv());
};

const transitionToProgramScreenFromPlaybackScreen = () => (dispatch: Function) => {
    const { contentId } = getSharedPreferencesAndRecordType();
    const oVideoPlaybackController = VideoPlaybackController.getInstance();
    const recordingId = oVideoPlaybackController.getWatchAction()?.kernel.recordingId || '';

    let id: string = '';
    let contentType: ContentType;

    if (oVideoPlaybackController.isLive()) {
        id = oVideoPlaybackController.getMetadata().airingOffers?.[0]?.offerId ?? '';
        contentType = ContentType.OFFER_ID;
    } else if (oVideoPlaybackController.isSocu()) {
        id = contentId;
        contentType = ContentType.CONTENT_ID;
    } else if (oVideoPlaybackController.isVod()) {
        id = oVideoPlaybackController.getMetadata().watchVodAction?.kernel?.offerId ?? '';
        contentType = ContentType.VOD_OFFER_ID;
    } else {
        id = recordingId;
        contentType = ContentType.RECORDING_ID;
    }

    const isVodEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_VOD_PLAYBACK_REMOVE_IN_KST_12166,
    );

    if (oVideoPlaybackController.isVod() && !isVodEnabled) {
        return;
    }
    dispatch(programScreenTransition({ id: id ?? '', contentType }));
};

const getSharedPreferencesAndRecordType = () => {
    const sharedPrefs = new SharedPreferences(SharedPrefConstants.RECORDING_ACTION_PREF_KEY);
    const recordKernelType = sharedPrefs.getString(
        SharedPrefConstants.RECORDING_ACTION_KERNEL_TYPE,
    );
    const contentId = sharedPrefs.getString(SharedPrefConstants.RECORDING_CONTENT_ID);
    const offerId = sharedPrefs.getString(SharedPrefConstants.RECORDING_OFFER_ID);
    const recordType = Locale.getString(LocaleKeys[recordKernelType]);

    return { sharedPrefs, recordType, contentId, offerId };
};

const showRecordOverlay = () => (dispatch: Function) => {
    const { sharedPrefs, recordType } = getSharedPreferencesAndRecordType();
    const ndvrInteractor = new NdvrInteractor();

    const actionKernelType = sharedPrefs.getString(SharedPrefConstants.RECORDING_ACTION_KERNEL);
    const contentId = sharedPrefs.getString(SharedPrefConstants.RECORDING_CONTENT_ID);
    const offerId = sharedPrefs.getString(SharedPrefConstants.RECORDING_OFFER_ID);

    const diskSpaceSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.DISK_SPACE_PREF_KEY,
    ).edit();
    // saving disk space info for storage limit overlay
    ndvrInteractor
        .getDiskSpace()
        .then((data: TStorageInfo) => {
            diskSpaceSharedPreferenceEditor.putNumber(
                SharedPrefConstants.DISK_SPACE_PERCENTAGE_USED,
                data.storageInfo.percentageUsed,
            );
            diskSpaceSharedPreferenceEditor.commit();
        })
        .catch((error) => {
            Log.e(TAG, 'Error while fetching disk space', error);
        });

    dispatch(showRecordingOverlay(recordType, { contentId, offerId }, actionKernelType));
};

export const onKeyRightActionsStrip = () => async (dispatch: Function, getState: Function) => {
    const { infoBanner } = getState();
    const { actionsStripHighlightIndex, actionItemsCount, actionStripReference } = infoBanner;
    if (actionsStripHighlightIndex >= actionItemsCount - 1) {
        // Circular action, go to first item
        await dispatch(setActionsStripHighlightIndex(0));
        updateActionStripMarginByIndex(0, actionStripReference);

        return;
    }
    const nextIndex = actionsStripHighlightIndex + 1;
    await dispatch(setActionsStripHighlightIndex(nextIndex));

    updateActionStripMarginByIndex(nextIndex, actionStripReference);
};

const updateActionStripMarginByIndex = (
    actionIndex: number,
    actionStripReference: React.RefObject<HTMLDivElement>,
) => {
    if (!actionStripReference?.current) return;
    let requiredMargin = 0;

    const { children } = actionStripReference.current;

    for (let i = 0; i < actionIndex; i += 1) {
        requiredMargin -= children[i].clientWidth;
    }

    actionStripReference.current.style.marginLeft = `${requiredMargin + DimensionScaler.getInstance().getScaledWidth(ACTION_STRIP_HIGHLIGHT_LEFT_OFFSET)}px`;
};
